import { Box, Button, Dialog, DialogContent, IconButton, Typography } from "@mui/material"
import { blueGrey, grey } from "@mui/material/colors"
import { alertSuccess } from "components/Alert"
import { BoxFC, BoxFR } from "components/BoxCustom"
import FileDropPanel from "components/FileUplaod/FileDropPanel"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { jobOrderApiNm } from "constants/jobOrderConstant"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import useCompactLayout from "hooks/useCompactLayout"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { CameraAltRounded, CloseRounded, DescriptionRounded, FolderOpenRounded } from "resource/muiImport"
import isFileOverSize from "utils/isFileOverSize"
import { resizeImage } from "utils/resizeImage"

/** Same checks as FileDropPanel: size limit, label and a preview link per file. */
const prepareFiles = (fileList) => {
  const files = Array.from(fileList)
  for (const file of files) {
    if (isFileOverSize(file)) return []
    file.label = file.name
    file.imgLink = URL.createObjectURL(file)
  }
  return files
}

/** Touch layout: one photo type with camera / file buttons and thumbnails of the picked files. */
const PickRow = ({ label, files, setFiles, disabled }) => {
  const handlePick = useCallback((e) => {
    const picked = prepareFiles(e.target.files)
    e.target.value = "" // lets the same photo be picked again after removing it
    if (picked.length > 0) setFiles(o => [...o, ...picked])
  }, [setFiles])

  const handleRemove = useCallback((index) => () => {
    setFiles(o => o.filter((_, i) => i !== index))
  }, [setFiles])

  return (
    <Box component="section" aria-label={label} sx={{ bgcolor: "background.paper", borderRadius: 2, p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, flex: 1, minWidth: 96 }}>
          {label}
          {files.length > 0 ?
            <Box component="span" sx={{ ml: 1, fontWeight: 400, color: "primary.main" }}>{files.length} ไฟล์</Box>
            : null}
        </Typography>
        <Button variant="contained" component="label" startIcon={<CameraAltRounded />} disabled={disabled} sx={{ minHeight: 40 }}>
          ถ่ายรูป
          <input type="file" hidden accept="image/*" capture="environment" onChange={handlePick} />
        </Button>
        <Button variant="outlined" component="label" startIcon={<FolderOpenRounded />} disabled={disabled} sx={{ minHeight: 40 }}>
          เลือกไฟล์
          <input type="file" hidden multiple onChange={handlePick} />
        </Button>
      </Box>
      {files.length > 0 ?
        <Box sx={{ display: "flex", gap: 1, overflowX: "auto", mt: 1.5, pb: 0.5 }}>
          {files.map((file, index) => (
            <Box key={`${file.name}-${index}`} sx={{
              position: "relative", width: 88, height: 88, flexShrink: 0, borderRadius: 1.5, overflow: "hidden",
              bgcolor: blueGrey[50], display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              {file.type?.startsWith("image/") ?
                <img src={file.imgLink} alt={file.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                :
                <Box sx={{ textAlign: "center", px: 0.5 }}>
                  <DescriptionRounded sx={{ color: grey[600] }} />
                  <Typography noWrap sx={{ fontSize: 11, maxWidth: 80 }}>{file.label}</Typography>
                </Box>
              }
              <IconButton size="small" aria-label={`เอา ${file.label} ออก`} onClick={handleRemove(index)} disabled={disabled}
                sx={{
                  position: "absolute", top: 2, right: 2, p: 0.5, color: "white", bgcolor: "rgba(0,0,0,0.6)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" }
                }}>
                <CloseRounded fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
        : null
      }
    </Box>
  )
}

const AddJobOrderFileDialog = ({ selectedId, dialogOpen, setDialogOpen, onFinish }) => {

  const { ax } = useContext(GlobalStateContext)
  const isCompact = useCompactLayout()
  const [contNoFiles, setContNoFiles] = useState([])
  const [sealNoFiles, setSealNoFiles] = useState([])
  const [docFiles, setDocFiles] = useState([])
  const [otherFiles, setOtherFiles] = useState([])
  const [contConFiles, setContConFiles] = useState([])
  const [cargoFiles, setCargoFiles] = useState([])
  const [locationFiles, setLocationFiles] = useState([])
  const [progress, setProgress] = useState(null) // { done, total } while uploading

  const uploadFile = useCallback(async (file, jobOrdFileTypId, jobOrdId, drvId) => {

    let label = ""
    if (jobOrdFileTypId === 1) {
      label = "หมายเลขตู้"
    } else if (jobOrdFileTypId === 2) {
      label = "หมายเลขซีล"
    } else if (jobOrdFileTypId === 3) {
      label = "สภาพตู้"
    } else if (jobOrdFileTypId === 4) {
      label = "รูปอื่นๆ"
    } else if (jobOrdFileTypId === 14) {
      label = "เอกสาร"
    } else if (jobOrdFileTypId === 17) {
      label = "สินค้า"
    } else if (jobOrdFileTypId === 18) {
      label = "สถานที่"
    }
    // const resizedFile  = await resizeFile(file)
    const resizedFile = await resizeImage(file)
    const formData = new FormData()
    formData.append("file", resizedFile)
    formData.append("Label", label)
    formData.append("JobOrdFileTypId", jobOrdFileTypId)
    formData.append("JobOrdId", jobOrdId)

    return ax.post(jobOrderApiNm.uploadJobOrderFile, formData)
  }, [ax])
  const handleSave = useCallback(async () => {
    if (progress) return
    // upload order by file type: 1, 2, 14, 3, 4, 17, 18
    const queue = [
      ...contNoFiles.map(file => [file, 1]),
      ...sealNoFiles.map(file => [file, 2]),
      ...docFiles.map(file => [file, 14]),
      ...contConFiles.map(file => [file, 3]),
      ...otherFiles.map(file => [file, 4]),
      ...cargoFiles.map(file => [file, 17]),
      ...locationFiles.map(file => [file, 18]),
    ]
    setProgress({ done: 0, total: queue.length })
    try {
      for (let i = 0; i < queue.length; i++) {
        await uploadFile(queue[i][0], queue[i][1], selectedId)
        setProgress({ done: i + 1, total: queue.length })
      }
    } finally {
      setProgress(null)
    }

    alertSuccess("บันทึกเรียบร้อยแล้ว")
    setDialogOpen(false)
    onFinish()
  }, [contNoFiles, sealNoFiles, docFiles, contConFiles, otherFiles, cargoFiles, locationFiles, selectedId, setDialogOpen
    , uploadFile, onFinish, progress])
  useEffect(() => {
    if (!dialogOpen) {
      setContNoFiles([])
      setSealNoFiles([])
      setOtherFiles([])
      setContConFiles([])
      setDocFiles([])
      setCargoFiles([])
      setLocationFiles([])
    }
  }, [dialogOpen, ax])

  if (isCompact) {
    const groups = [
      { label: "หมายเลขตู้", files: contNoFiles, setFiles: setContNoFiles },
      { label: "หมายเลขซีล", files: sealNoFiles, setFiles: setSealNoFiles },
      { label: "เอกสาร", files: docFiles, setFiles: setDocFiles },
      { label: "สภาพตู้", files: contConFiles, setFiles: setContConFiles },
      { label: "สินค้า", files: cargoFiles, setFiles: setCargoFiles },
      { label: "สถานที่", files: locationFiles, setFiles: setLocationFiles },
      { label: "รูปอื่นๆ", files: otherFiles, setFiles: setOtherFiles },
    ]
    const total = groups.reduce((sum, group) => sum + group.files.length, 0)
    return (
      <Dialog open={dialogOpen} fullScreen aria-labelledby="ajof-compact-title"
        onClose={() => { if (!progress) setDialogOpen(false) }}
        PaperProps={{ sx: { bgcolor: blueGrey[50] } }}>
        <Box sx={{
          display: "flex", alignItems: "center", gap: 0.5, px: 0.5, pb: 0.5,
          pt: "calc(4px + env(safe-area-inset-top))", bgcolor: "primary.main", color: "primary.contrastText"
        }}>
          <IconButton color="inherit" aria-label="ปิด" onClick={() => setDialogOpen(false)} disabled={!!progress}>
            <CloseRounded />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <Typography id="ajof-compact-title" noWrap sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.3 }}>เพิ่มรูปใบงาน</Typography>
            <Typography noWrap sx={{ fontSize: 13, opacity: 0.85 }}>ใบงาน {selectedId}</Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", p: 1, display: "grid", gap: 1, alignContent: "start",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" } }}>
          {groups.map(group => (
            <PickRow key={group.label} label={group.label} files={group.files} setFiles={group.setFiles} disabled={!!progress} />
          ))}
        </Box>
        <Box sx={{
          display: "flex", gap: 1, px: 1, pt: 1, pb: "calc(8px + env(safe-area-inset-bottom))",
          bgcolor: "background.paper", borderTop: 1, borderColor: "divider"
        }}>
          <Button variant="outlined" color="inherit" sx={{ flex: 1, minHeight: 48 }} disabled={!!progress}
            onClick={() => setDialogOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" sx={{ flex: 2, minHeight: 48 }} disabled={total === 0 || !!progress} onClick={handleSave}>
            {progress ? `กำลังอัปโหลด ${progress.done}/${progress.total}` : total > 0 ? `บันทึก ${total} ไฟล์` : "บันทึก"}
          </Button>
        </Box>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return
        setDialogOpen(false)
      }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xl' >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={"เพิ่มรูปใบงาน"}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFR mt={2} sx={{ justifyContent: "space-evenly", alignItems: "stretch" }} height={400}>
          <BoxFC width="100%" sx={{ gap: 0 }}>
            <Typography variant="h6">หมายเลขตู้</Typography>
            <FileDropPanel height="100%" selectedFiles={contNoFiles} setSelectedFiles={setContNoFiles} />
          </BoxFC>
          <BoxFC width="100%" sx={{ gap: 0 }}>
            <Typography variant="h6">หมายเลขซีล</Typography>
            <FileDropPanel height="100%" selectedFiles={sealNoFiles} setSelectedFiles={setSealNoFiles} />
          </BoxFC>
          <BoxFC width="100%" sx={{ gap: 0 }}>
            <Typography variant="h6">เอกสาร</Typography>
            <FileDropPanel height="100%" selectedFiles={docFiles} setSelectedFiles={setDocFiles} />
          </BoxFC>
          <BoxFC width="100%" sx={{ gap: 0 }}>
            <Typography variant="h6">สภาพตู้</Typography>
            <FileDropPanel height="100%" selectedFiles={contConFiles} setSelectedFiles={setContConFiles} />
          </BoxFC>
          <BoxFC width="100%" sx={{ gap: 0 }}>
            <Typography variant="h6">สินค้า</Typography>
            <FileDropPanel height="100%" selectedFiles={cargoFiles} setSelectedFiles={setCargoFiles} />
          </BoxFC>
          <BoxFC width="100%" sx={{ gap: 0 }}>
            <Typography variant="h6">สถานที่</Typography>
            <FileDropPanel height="100%" selectedFiles={locationFiles} setSelectedFiles={setLocationFiles} />
          </BoxFC>
          <BoxFC width="100%" sx={{ gap: 0 }}>
            <Typography variant="h6">รูปอื่นๆ</Typography>
            <FileDropPanel height="100%" selectedFiles={otherFiles} setSelectedFiles={setOtherFiles} />
          </BoxFC>

        </BoxFR>
      </DialogContent>
      <DialogFooter okText={progress ? `กำลังอัปโหลด ${progress.done}/${progress.total}` : "บันทึก"}
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleSave} />
    </Dialog>
  )
}

export default React.memo(AddJobOrderFileDialog, (o, n) => o.dialogOpen === n.dialogOpen)
